import React, { useEffect, useMemo, useState } from "react";
import { FaPen, FaPlusCircle, FaSearch, FaShieldAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { getCachedOrFetch, getTenantCacheKey, invalidateCache } from "../utils/requestCache";
import { useAppTheme } from "../context/AppThemeContext";
import AdminResponsiveTabs from "./AdminResponsiveTabs";
import {
  ROLE_ADMIN,
  ROLE_PARENT,
  ROLE_PRINCIPAL,
  ROLE_SCHOOL,
  ROLE_STAFF,
  ROLE_STUDENT,
  ROLE_SUPER_ADMIN,
  ROLE_TEACHER,
} from "../utils/authRole";
import {
  extractPermissionRules,
  normalizePermissionRule,
  syncPermissionRules,
} from "../utils/permissionState";
import AdminModalShell from "./AdminModalShell";
import AdminMobileViewToggle from "./AdminMobileViewToggle";

const emptyForm = {
  id: "",
  role: ROLE_ADMIN,
  module: "",
  canView: true,
  canCreate: false,
  canEdit: false,
  canDelete: false,
  approvalRequired: false,
};

const badgeTone = (enabled) => (
  enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
);

export default function AdminPermissionMatrix() {
  const { isDarkMode } = useAppTheme();
  const [activeTab, setActiveTab] = useState("table");
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dataSource, setDataSource] = useState("api");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [mobileRecordView, setMobileRecordView] = useState("cards");
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    const fetchPermissions = async () => {
      setLoading(true);
      try {
        const response = await getCachedOrFetch(getTenantCacheKey("admin:permissions"), () => api.get(API_PATHS.admin.permissions()));
        const nextRecords = extractPermissionRules(response.data);
        if (nextRecords.length > 0) {
          setPermissions(nextRecords);
          setSelectedId(nextRecords[0].id);
          setDataSource("api");
          syncPermissionRules(nextRecords);
        } else {
          setPermissions([]);
          setSelectedId("");
          setDataSource("unavailable");
        }
      } catch {
        // Permission rules are security-sensitive. Never use browser storage
        // or demo rules as a fallback after the server cannot be verified.
        setPermissions([]);
        setSelectedId("");
        setDataSource("unavailable");
      } finally {
        setLoading(false);
      }
    };
    fetchPermissions();
  }, []);

  const selectedRule = useMemo(() => (
    permissions.find((item) => item.id === selectedId) || null
  ), [permissions, selectedId]);

  useEffect(() => {
    setForm(selectedRule || emptyForm);
  }, [selectedRule]);

  const filteredPermissions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return permissions.filter((item) => (
      !query
      || item.role.toLowerCase().includes(query)
      || item.module.toLowerCase().includes(query)
    ));
  }, [permissions, searchTerm]);

  const summary = useMemo(() => ({
    total: permissions.length,
    protected: permissions.filter((item) => item.approvalRequired).length,
    fullAccess: permissions.filter((item) => item.canView && item.canCreate && item.canEdit && item.canDelete).length,
  }), [permissions]);

  const updateForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const resetForm = () => {
    setSelectedId("");
    setForm(emptyForm);
  };
  const openCreateForm = () => {
    resetForm();
    setIsFormOpen(true);
  };
  const openEditForm = (id) => {
    setSelectedId(id);
    setIsFormOpen(true);
  };

  const upsertPermission = (nextRecord) => {
    setPermissions((current) => {
      const exists = current.some((item) => item.id === nextRecord.id);
      const next = !exists
        ? [nextRecord, ...current]
        : current.map((item) => (item.id === nextRecord.id ? nextRecord : item));
      syncPermissionRules(next);
      return next;
    });
    setSelectedId(nextRecord.id);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    const payload = {
      role: form.role,
      module: form.module.trim(),
      canView: form.canView,
      canCreate: form.canCreate,
      canEdit: form.canEdit,
      canDelete: form.canDelete,
      approvalRequired: form.approvalRequired,
    };
    const isEditing = Boolean(form.id);

    try {
      const response = isEditing
        ? await api.put(API_PATHS.admin.permissionById(form.id), payload)
        : await api.post(API_PATHS.admin.permissions(), payload);

      const nextRecord = normalizePermissionRule(
        { ...(response.data?.data ?? response.data ?? {}), ...payload, id: isEditing ? form.id : undefined },
        permissions.length
      );
      upsertPermission(nextRecord);
      invalidateCache(getTenantCacheKey("admin:permissions"));
      setIsFormOpen(false);
      setDataSource("api");
      toast.success(isEditing ? "Permission updated." : "Permission created.");
    } catch (error) {
      toast.error(error?.message || "Permission changes could not be verified by the server.");
    } finally {
      setSaving(false);
    }
  };

  const toggleCheckbox = (key) => {
    setForm((current) => ({ ...current, [key]: !current[key] }));
  };

  return (
    <div className={`space-y-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
      <AdminResponsiveTabs tabs={[{ key: "table", label: "Permissions" }, { key: "summary", label: "Summary" }]} activeKey={activeTab} onChange={setActiveTab} tone="amber" isDarkMode={isDarkMode} />

      <div className="border-x border-b border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/60">
        {dataSource === "unavailable" && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Permission rules could not be verified by the server. No browser or demo rules were loaded.
          </div>
        )}

        {activeTab === "table" && (
          <>
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Permissions</p>
                <h2 className="mt-1 text-2xl font-bold text-slate-900">Role access matrix</h2>
              </div>
              <div className="flex w-full gap-2 md:w-auto">
                <div className="relative w-full md:w-72">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="w-full rounded-xl border border-slate-300 py-2.5 pl-10 pr-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100"
                    placeholder="Search role or module"
                  />
                </div>
                <button
                  type="button"
                  onClick={openCreateForm}
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600"
                >
                  <FaPlusCircle />
                  Add
                </button>
              </div>
            </div>

            <div className="mb-3"><AdminMobileViewToggle value={mobileRecordView} onChange={setMobileRecordView} isDarkMode={isDarkMode} /></div>
            {mobileRecordView === "cards" && (
              <div className="space-y-3 md:hidden">
                {loading ? <p className="rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-500">Loading permission rules...</p> : filteredPermissions.length === 0 ? <p className="rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-500">No rules found.</p> : filteredPermissions.map((item) => (
                  <article key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3"><div><h3 className="font-bold text-slate-900">{item.role}</h3><p className="mt-1 text-xs text-slate-500">{item.module}</p></div><span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${badgeTone(item.approvalRequired)}`}>{item.approvalRequired ? "Approval" : "Standard"}</span></div>
                    <div className="mt-3 grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-3 text-xs text-slate-600"><span>View: <b>{item.canView ? "Yes" : "No"}</b></span><span>Create: <b>{item.canCreate ? "Yes" : "No"}</b></span><span>Edit: <b>{item.canEdit ? "Yes" : "No"}</b></span><span>Delete: <b>{item.canDelete ? "Yes" : "No"}</b></span></div>
                    <button type="button" onClick={() => openEditForm(item.id)} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700"><FaPen /> Update rule</button>
                  </article>
                ))}
              </div>
            )}

            <div className={`${mobileRecordView === "cards" ? "hidden md:block" : "block"} overflow-hidden rounded-2xl border border-slate-200`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-100 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Role</th>
                      <th className="px-4 py-3 text-left font-semibold">Module</th>
                      <th className="px-4 py-3 text-left font-semibold">View</th>
                      <th className="px-4 py-3 text-left font-semibold">Create</th>
                      <th className="px-4 py-3 text-left font-semibold">Edit</th>
                      <th className="px-4 py-3 text-left font-semibold">Delete</th>
                      <th className="px-4 py-3 text-left font-semibold">Approval</th>
                      <th className="px-4 py-3 text-left font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white text-slate-800">
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-slate-500">Loading permission rules...</td>
                      </tr>
                    ) : filteredPermissions.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-slate-500">No rules found.</td>
                      </tr>
                    ) : filteredPermissions.map((item) => (
                      <tr key={item.id} className="border-t border-slate-200 hover:bg-slate-50">
                        <td className="px-4 py-3 font-semibold">{item.role}</td>
                        <td className="px-4 py-3">{item.module}</td>
                        <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${badgeTone(item.canView)}`}>{item.canView ? "Yes" : "No"}</span></td>
                        <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${badgeTone(item.canCreate)}`}>{item.canCreate ? "Yes" : "No"}</span></td>
                        <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${badgeTone(item.canEdit)}`}>{item.canEdit ? "Yes" : "No"}</span></td>
                        <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${badgeTone(item.canDelete)}`}>{item.canDelete ? "Yes" : "No"}</span></td>
                        <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${badgeTone(item.approvalRequired)}`}>{item.approvalRequired ? "Required" : "No"}</span></td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => openEditForm(item.id)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                          >
                            <FaPen />
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === "summary" && (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Summary</p>
              <h2 className="mt-1 text-2xl font-bold text-slate-900">Permission coverage</h2>
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {[
                { label: "Total rules", value: summary.total },
                { label: "Approval required", value: summary.protected },
                { label: "Full access", value: summary.fullAccess },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{item.label}</p>
                    <FaShieldAlt className="text-slate-400" />
                  </div>
                  <p className="mt-2 text-2xl font-bold text-slate-900">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <AdminModalShell isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} isDarkMode={isDarkMode} maxWidth="max-w-3xl">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg">
          <h3 className="text-xl font-bold text-slate-900">{form.id ? "Update Permission Rule" : "Create Permission Rule"}</h3>
          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Role</label>
                <select value={form.role} onChange={(event) => updateForm("role", event.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm">
                  <option value={ROLE_ADMIN}>Admin</option>
                  <option value={ROLE_SUPER_ADMIN}>Super Admin</option>
                  <option value={ROLE_SCHOOL}>School Admin</option>
                  <option value={ROLE_PRINCIPAL}>Principal</option>
                  <option value={ROLE_TEACHER}>Teacher</option>
                  <option value={ROLE_STAFF}>Staff</option>
                  <option value={ROLE_PARENT}>Parent</option>
                  <option value={ROLE_STUDENT}>Student</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Module</label>
                <input
                  type="text"
                  value={form.module}
                  onChange={(event) => updateForm("module", event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
                  placeholder="Fees, Admissions, Timetable"
                  required
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {[
                { key: "canView", label: "Can view" },
                { key: "canCreate", label: "Can create" },
                { key: "canEdit", label: "Can edit" },
                { key: "canDelete", label: "Can delete" },
                { key: "approvalRequired", label: "Approval required" },
              ].map((item) => (
                <label key={item.key} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium">
                  <span>{item.label}</span>
                  <input
                    type="checkbox"
                    checked={form[item.key]}
                    onChange={() => toggleCheckbox(item.key)}
                    className="h-4 w-4"
                  />
                </label>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={saving} className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-70">
                {saving ? "Saving..." : form.id ? "Update" : "Create"}
              </button>
              <button type="button" onClick={resetForm} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                Reset
              </button>
            </div>
          </form>
        </div>
      </AdminModalShell>
    </div>
  );
}
